<!--
  SupportReplyTemplates.svelte — the team's reusable replies.

  A reply template is scoped to one product or offered for all of them, and its
  body may carry placeholders (`{{FIRST_NAME}}`, `{{TICKET_KEY}}` …) that the
  composer fills in from the ticket as the reply is inserted. The editor lists
  the placeholders beside the body and inserts one on click, because a typo in a
  placeholder name is only discovered in front of a customer otherwise.
-->
<script lang="ts">
	import { onMount } from 'svelte';
	import {
		Badge,
		Button,
		Input,
		Select,
		Spinner,
		Table,
		TableBody,
		TableBodyCell,
		TableBodyRow,
		TableHead,
		TableHeadCell,
		Textarea
	} from 'flowbite-svelte';
	import AbstractCard from '../../components/AbstractCard.svelte';
	import { ConfirmButton, EmptyState, FormField, PageHeader, RelativeTime } from '../../components';
	import {
		createReplyTemplate,
		deleteReplyTemplate,
		getSupportOverview,
		listReplyTemplates,
		updateReplyTemplate
	} from '../../AppNetwork';
	import type { SupportReplyTemplate, SupportProduct } from '../../AppTypes';
	import { REPLY_TEMPLATE_PLACEHOLDERS } from '../../support/supportFormat';
	import { toastError, toastSuccess } from '../../toast';
	import { t, t2 } from '../../i18n';

	/** Every reply template. */
	let replies: SupportReplyTemplate[] = $state([]);
	/** Every support-enabled product, for scoping. */
	let products: SupportProduct[] = $state([]);
	/** Whether the list is loading. */
	let loading = $state(true);
	/** Failure of the list. */
	let error = $state('');

	/** Whether the editor is open. */
	let editing = $state(false);
	/** The reply being edited, or `null` for a new one. */
	let editingUUID = $state<string | null>(null);
	/** Editor: title. */
	let title = $state('');
	/** Editor: body. */
	let body = $state('');
	/** Editor: product scope; empty is every product. */
	let scope = $state('');
	/** Whether a save is in flight. */
	let saving = $state(false);

	/** Loads the replies and the products they can be scoped to. */
	async function load(): Promise<void> {
		loading = true;
		error = '';
		/** Without a product filter the endpoint returns every reply. */
		try {
			const [list, overview] = await Promise.all([listReplyTemplates(), getSupportOverview()]);
			replies = list;
			products = overview.products;
		} catch (e) {
			error = e instanceof Error ? e.message : t2('Failed to load templates');
		} finally {
			loading = false;
		}
	}

	/** Opens the editor, empty or on an existing reply. */
	function openEditor(reply: SupportReplyTemplate | null): void {
		editingUUID = reply?.templateUUID ?? null;
		title = reply?.title ?? '';
		body = reply?.body ?? '';
		scope = reply?.applicationUUID ?? '';
		editing = true;
	}

	/** Closes the editor without saving. */
	function closeEditor(): void {
		editing = false;
		editingUUID = null;
	}

	/** Appends a placeholder to the body. */
	function insertPlaceholder(key: string): void {
		body = `${body}{{${key}}}`;
	}

	/** Whether the editor holds something saveable. */
	let canSave = $derived(title.trim().length > 0 && body.trim().length > 0 && !saving);

	/** Saves the editor — a create or an update. */
	async function save(): Promise<void> {
		saving = true;
		const input = { title: title.trim(), body, applicationUUID: scope || null };
		/** Create or update, then re-read the list so ordering is the server's. */
		try {
			await (editingUUID ? updateReplyTemplate(editingUUID, input) : createReplyTemplate(input));
			toastSuccess(t2('Template saved.'));
			closeEditor();
			replies = await listReplyTemplates();
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to save the template'));
		} finally {
			saving = false;
		}
	}

	/** Deletes one reply. */
	async function remove(reply: SupportReplyTemplate): Promise<void> {
		/** Removed from the list only once the server confirms. */
		try {
			await deleteReplyTemplate(reply.templateUUID);
			replies = replies.filter((r) => r.templateUUID !== reply.templateUUID);
			toastSuccess(t2('Template deleted.'));
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to delete the template'));
		}
	}

	/** The name of the product a reply is scoped to. */
	function productName(applicationUUID: string | null): string {
		/* Unscoped replies are offered on every product's tickets. */
		if (!applicationUUID) return t2('All products');
		return products.find((p) => p.applicationUUID === applicationUUID)?.name ?? applicationUUID;
	}

	/** Scope items. */
	let scopeItems = $derived([
		{ value: '', name: $t('All products') },
		...products.map((product) => ({ value: product.applicationUUID, name: product.name }))
	]);

	onMount(load);
</script>

<PageHeader
	title={$t('Templates')}
	description={$t('Replies the team reuses. Placeholders are filled in from the ticket when a reply is inserted.')}
	breadcrumbs={[{ label: $t('Support Desk'), href: '/Support/Desk' }, { label: $t('Templates') }]}
/>

<div class="space-y-6">
	{#if !editing}
		<div class="-mt-4 flex justify-end">
			<Button onclick={() => openEditor(null)}><i class="fa-solid fa-plus me-2"></i>{$t('New template')}</Button>
		</div>
	{:else}
		<AbstractCard class="max-w-none">
			<p class="card-label mb-3">{editingUUID ? $t('Edit template') : $t('New template')}</p>
			<div class="grid gap-x-4 md:grid-cols-[1fr_16rem]">
				<FormField label={$t('Title')} id="template-title" required>
					<Input id="template-title" bind:value={title} placeholder={$t('Ask for logs')} />
				</FormField>
				<FormField label={$t('Product')} id="template-scope">
					<Select
						id="template-scope"
						placeholder=""
						items={scopeItems}
						value={scope}
						onchange={(event: Event) => (scope = (event.currentTarget as HTMLSelectElement).value)}
					/>
				</FormField>
			</div>
			<FormField label={$t('Reply')} id="template-body" required>
				<Textarea id="template-body" rows={8} divClass="!w-full" class="!w-full" bind:value={body} />
			</FormField>
			<div class="mb-4 flex flex-wrap items-center gap-2">
				<span class="text-default text-xs">{$t('Insert placeholder:')}</span>
				{#each REPLY_TEMPLATE_PLACEHOLDERS as placeholder (placeholder.key)}
					<button
						type="button"
						class="bg-depth depth-hover rounded-md px-2 py-0.5 font-mono text-xs"
						title={$t(placeholder.description)}
						onclick={() => insertPlaceholder(placeholder.key)}
					>
						{`{{${placeholder.key}}}`}
					</button>
				{/each}
			</div>
			<div class="flex justify-end gap-2">
				<Button color="alternative" onclick={closeEditor}>{$t('Cancel')}</Button>
				<Button disabled={!canSave} onclick={save}>
					{#if saving}<Spinner size="4" class="me-2" />{/if}{$t('Save')}
				</Button>
			</div>
		</AbstractCard>
	{/if}

	{#if loading}
		<EmptyState variant="loading" />
	{:else if error}
		<EmptyState variant="error" description={error} />
	{:else if replies.length === 0}
		<EmptyState
			faIcon="comment-dots"
			title={$t('No templates yet')}
			description={$t('Save the answers the team gives most often, and insert them from any ticket.')}
		/>
	{:else}
		<Table divClass="widget !p-1 relative" class="dataTable">
			<TableHead>
				<TableHeadCell>{$t('Title')}</TableHeadCell>
				<TableHeadCell class="w-44">{$t('Product')}</TableHeadCell>
				<TableHeadCell class="w-36">{$t('Updated')}</TableHeadCell>
				<TableHeadCell class="w-px !px-2"><span class="sr-only">{$t('Actions')}</span></TableHeadCell>
			</TableHead>
			<TableBody class="divide-y divide-gray-200 dark:divide-gray-700">
				{#each replies as reply (reply.templateUUID)}
					<TableBodyRow class="hover:bg-gray-50 dark:hover:bg-gray-600">
						<TableBodyCell>
							<span class="font-medium text-gray-900 dark:text-white">{reply.title}</span>
							<p class="text-default line-clamp-2 text-xs">{reply.body}</p>
						</TableBodyCell>
						<TableBodyCell>
							<Badge color={reply.applicationUUID ? 'blue' : 'gray'}>{productName(reply.applicationUUID)}</Badge>
						</TableBodyCell>
						<TableBodyCell class="text-default text-sm"><RelativeTime date={reply.updated} /></TableBodyCell>
						<TableBodyCell class="!px-2">
							<div class="flex gap-1">
								<Button
									size="xs"
									color="alternative"
									class="!p-2"
									title={$t('Edit')}
									aria-label={$t('Edit {{name}}', { name: reply.title })}
									onclick={() => openEditor(reply)}
								>
									<i class="fa-solid fa-pen-to-square h-3 w-3"></i>
								</Button>
								<ConfirmButton
									size="xs"
									color="alternative"
									class="!p-2"
									confirmText={$t('Delete the template “{{name}}”?', { name: reply.title })}
									onconfirm={() => remove(reply)}
								>
									<i class="fa-solid fa-trash h-3 w-3 text-red-600"></i>
								</ConfirmButton>
							</div>
						</TableBodyCell>
					</TableBodyRow>
				{/each}
			</TableBody>
		</Table>
	{/if}
</div>
